import { useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Box, useTheme, Typography } from '@mui/material';
import { formatCurrency } from '../utils/formatters';

const HeatMapCell = ({ value, type, maxValue, theme }) => {
    if (!value || value === 0) {
        return <Box sx={{ width: '100%', height: 30, backgroundColor: '#f5f5f5' }} />;
    }

    // TODO: consider when maxValue is zero - devision by zero
    const intensity = maxValue > 0 ? Math.min(value / maxValue, 1) : 0;
    
    const getHeatmapColor = (type, intensity) => {

        // Manual calculation of heatmap colour
        const baseIntensity = 0.3 + intensity * 0.7;
        switch (type) {
            case 'critical':
                return `rgba(244, 67, 54, ${baseIntensity})`;
            case 'toReview':
                return `rgba(255, 152, 0, ${baseIntensity})`;
            case 'onTrack':
                return `rgba(76, 175, 80, ${baseIntensity})`;
            default:
                return `rgba(158, 158, 158, ${baseIntensity})`;
        }
    };

    const getBorderColor = (type) => {
        switch (type) {
            case 'critical':
                return theme.palette.status.critical;
            case 'toReview':
                return theme.palette.status.warning;
            case 'onTrack':
                return theme.palette.status.success;
            default:
                return theme.palette.status.neutral;
        }
    };

    return (
        <Box sx={{ 
            width: '100%', 
            height: 30, 
            backgroundColor: getHeatmapColor(type, intensity),
            border: `1px solid ${getBorderColor(type)}`,
            borderRadius: 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            <Typography variant="caption" sx={{ 
                color: intensity > 0.6 ? 'white' : getBorderColor(type),
                fontWeight: 'bold',
                fontSize: '0.7rem'
            }}>
                {value}
            </Typography>
        </Box>
    );
};

export const RecoverableMarginSummary = ({ data, onRowSelect }) => {
    const theme = useTheme();
    const [selectedId, setSelectedId] = useState(null);

    if (!data || data.length === 0) {
        return (
            <Paper sx={{ 
                backgroundColor: 'white',
                border: 'thin solid #323D7225',
                borderRadius: 2,
                boxShadow: '1px 1px #323D7250',
                p: 2 
            }}>
                <Typography variant="h6" color="primary" mb={2}>
                    Est. Recoverable Margin
                </Typography>
                <Typography align="center" color="textSecondary">
                    No data available
                </Typography>
            </Paper>
        );
    }

    const handleRowSelect = (row) => {
        setSelectedId(row.id);
        if (onRowSelect) {
            onRowSelect(row);
        }
    };

    const renderRow = (row) => {
        const isSelected = selectedId === row.id;

        const allValues = data.flatMap(r => [r, ...(r.subRows || [])]);
        const maxCritical = Math.max(...allValues.map(r => r.critical || 0));
        const maxToReview = Math.max(...allValues.map(r => r.toReview || 0));
        const maxOnTrack = Math.max(...allValues.map(r => r.onTrack || 0));

        return (
            <TableRow
                key={row.id}
                hover
                selected={isSelected}
                onClick={() => handleRowSelect(row)}
                sx={{
                    cursor: 'pointer',
                    '&:hover': {
                        backgroundColor: theme.palette.action.hover
                    }
                }}
            >
                <TableCell sx={{ width: 100 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {row.area}
                    </Box>
                </TableCell>

                <TableCell sx={{ width: 320 }}>
                    {row.description}
                </TableCell>

                <TableCell align="center" sx={{ width: 110, p: 1 }}>
                    <HeatMapCell 
                        value={row.critical} 
                        type="critical" 
                        maxValue={maxCritical}
                        theme={theme}
                    />
                </TableCell>

                <TableCell align="center" sx={{ width: 110, p: 1 }}>
                    <HeatMapCell 
                        value={row.toReview} 
                        type="toReview" 
                        maxValue={maxToReview}
                        theme={theme}
                    />
                </TableCell>

                <TableCell align="center" sx={{ width: 110, p: 1 }}>
                    <HeatMapCell 
                        value={row.onTrack} 
                        type="onTrack" 
                        maxValue={maxOnTrack}
                        theme={theme}
                    />
                </TableCell>

                <TableCell align="center">
                    {row.profitImpact ? formatCurrency(row.profitImpact) : ''}
                </TableCell>

                <TableCell align="center">
                    {row.profitImpact ? formatCurrency(row.profitImpact) : ''}
                </TableCell>

                <TableCell align="center">
                    {row.percentImpact ? `${row.percentImpact}%` : ''}
                </TableCell>

                <TableCell align="center">
                    {row.accounts || ''}
                </TableCell>

                <TableCell align="center">
                    {row.skus || ''}
                </TableCell>

                <TableCell align="center">
                    {row.salesReps || ''}
                </TableCell>

                <TableCell align="center">
                    {row.riskLevel || 'Medium'}
                </TableCell>

                <TableCell align="center">
                    {row.effortLevel || 'Medium'}
                </TableCell>
            </TableRow>
        );
    };

    const totals = data.reduce((acc, row) => {
        acc.profitImpact += row.profitImpact || 0;
        return acc;
    }, { profitImpact: 0 });

    const header_style = {
        fontWeight: 'bold', 
        color: theme.palette.primary.main
    };

    return (
        <Paper sx={{ 
            backgroundColor: 'white',
            border: 'thin solid #323D7225',
            borderRadius: 2,
            boxShadow: '1px 1px #323D7250',
            p: 2 
        }}>
            <Typography variant="h6" color="primary" mb={2}>
                Est. Recoverable Margin
            </Typography>

            <TableContainer>
                <Table size="small" sx={{ tableLayout: 'fixed' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell width={100} sx={header_style}>
                                Area
                            </TableCell>
                            <TableCell width={320} sx={header_style}>
                                Profit Leak Description
                            </TableCell>
                            <TableCell width={110} align="center" sx={header_style}>
                                Critical
                            </TableCell>
                            <TableCell width={110} align="center" sx={header_style}>
                                To-Review
                            </TableCell>
                            <TableCell width={110} align="center" sx={header_style}>
                                On-Track
                            </TableCell>
                            <TableCell align="center" sx={header_style}>
                                Total Margin Opportunity
                            </TableCell>
                            <TableCell align="center" sx={header_style}>
                                Est. Recoverable Margin
                            </TableCell>
                            <TableCell align="center" sx={header_style}>
                                % of Impact
                            </TableCell>
                            <TableCell align="center" sx={header_style}>
                                # Accounts
                            </TableCell>
                            <TableCell align="center" sx={header_style}>
                                # SKUs
                            </TableCell>
                            <TableCell align="center" sx={header_style}>
                                # Sales Reps
                            </TableCell>
                            <TableCell align="center" sx={header_style}>
                                Risk Level
                            </TableCell>
                            <TableCell align="center" sx={header_style}>
                                Effort Level
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.map(row => renderRow(row))}

                        <TableRow sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                            <TableCell colSpan={5} sx={{ fontWeight: 'bold' }}>
                                Total
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                {formatCurrency(totals.profitImpact)}
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                {formatCurrency(totals.profitImpact)}
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                100%
                            </TableCell>
                            <TableCell colSpan={5} />
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};